import type { AxiosError } from "axios";

export const SESSION_INVALIDATED_CODE = "SESSION_INVALIDATED_BY_NEW_LOGIN";
export const SESSION_INVALIDATED_BY_LOGOUT_CODE = "SESSION_INVALIDATED_BY_LOGOUT";

export const SESSION_INVALIDATED_MESSAGE =
  "You've been logged out because you logged in on another device or browser.";

export const isSessionInvalidatedError = (message: string): boolean =>
  message.includes("session has been invalidated") ||
  message.includes("SessionInvalidatedException");

/**
 * True when a rejected Axios error is a session-invalidation 401 (kicked by a
 * new login / remote logout), as opposed to a plain expired-token 401.
 *
 * Single source of truth for BOTH response interceptors: the session-kill
 * interceptor uses it to claim these errors, and the generic unauthorized
 * interceptor uses it to SKIP them (so the session flow — cleanup + "Session
 * Ended" overlay — owns the messaging regardless of interceptor order).
 * Keeping one predicate here means the two interceptors cannot drift.
 */
export const isSessionInvalidation401 = (error: AxiosError): boolean => {
  if (error.response?.status !== 401) {
    return false;
  }
  const data = error.response.data as { code?: string; message?: string } | undefined;
  return (
    data?.code === SESSION_INVALIDATED_CODE ||
    data?.code === SESSION_INVALIDATED_BY_LOGOUT_CODE ||
    (typeof data?.message === "string" && isSessionInvalidatedError(data.message))
  );
};
